import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router";

// Slides each new screen in, and resets scroll so a new page starts at the top.
//
// React Router's `viewTransition` option needs a data router
// (createBrowserRouter); this app uses BrowserRouter, so the animation is
// driven off the navigation type instead. That has the advantage of covering
// the browser's own back button, not just in-app navigation.
//
// Forward navigations slide in from the right; going back reverses it. Only
// the incoming screen animates — the outgoing one unmounts immediately, which
// the short duration and the fade make read as a single movement.

function readHistoryIndex(): number | null {
  const index = (window.history.state as { idx?: unknown } | null)?.idx;
  return typeof index === "number" ? index : null;
}

export default function PageTransition(props: { children: React.ReactNode }) {
  const location = useLocation();
  const navigationType = useNavigationType();

  // Null until the first navigation, so the very first page load does not
  // animate in.
  const previousKey = useRef<string | null>(null);

  // Back and forward are both POP, so the navigation type alone cannot tell
  // them apart. React Router stamps an incrementing `idx` onto history state;
  // comparing it against the last one gives the direction.
  const historyIndex = readHistoryIndex();
  const previousIndex = useRef<number | null>(historyIndex);

  useEffect(() => {
    previousKey.current = location.key;
    previousIndex.current = historyIndex;
  }, [location.key, historyIndex]);

  // A new screen starts at the top. Back and forward are left alone so the
  // browser can restore the position that history entry was at, which is what
  // people expect when returning to a list they had scrolled.
  //
  // Runs before paint, so the new page never flashes at the old scroll offset.
  useLayoutEffect(() => {
    if (navigationType === "POP") return;
    window.scrollTo(0, 0);
  }, [location.key, navigationType]);

  const hasNavigated = previousKey.current !== null;

  let direction: "forward" | "back" = "forward";
  if (navigationType === "POP") {
    const from = previousIndex.current;
    // Unknown index (older entry, or a shape change in the router) reads as
    // back, which is what POP is the overwhelming majority of the time.
    direction = from !== null && historyIndex !== null && historyIndex > from
      ? "forward"
      : "back";
  }

  return <div className="page-viewport">
    <div
      // Remounting on each location is what restarts the animation.
      key={location.key}
      className={hasNavigated ? `page-enter-${direction}` : undefined}
    >
      {props.children}
    </div>
  </div>
}
